import type Luminary from '@lumen5/luminary';

import type { ConversationState } from '../Conversation';
import { Conversation } from '../Conversation';
import { fetchLatestConversationData } from '../conversationData';
import type { Tool } from '../tools/Tool';
import type { EnhancedToolResult, MessageContent } from '../types';

import type { AnySkill } from 'js/src/agent/skills/types';
import type { SceneAddressing } from 'js/src/agent/tools/LintVideoTool';
import { setSaveConflict } from 'js/src/data/app/app-actions';
import { clearAllAgentActivity } from 'js/src/data/agent-activity';
import { getSaveConflict } from 'js/src/data/app/app-selectors';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import type { AppDispatch } from 'js/src/libs/RootState';
import uuid from 'js/src/libs/uuid';
import type { GetRootState } from 'js/src/luminary/luminary-redux-connect-types';

export interface RootAgentConfig {
    luminary: Luminary;
    dispatch: AppDispatch;
    model: string;
    getState: GetRootState;
    toolsOverride?: Tool[];
    requiresVideoState?: boolean;
    maxIterations?: number;
    videoId?: number;
    streaming?: boolean;
    enableCodeInterpreter?: boolean;
    enableThinkingSummaries?: boolean;
    /**
     * Reasoning effort (gpt-5.x) / thinking_level (gemini-3.x). For gpt-5.x,
     * anything above 'none' also routes through the Responses API.
     */
    reasoningEffort?: string;
    /**
     * How captured video state addresses scenes and scene groups (positional indices vs stable
     * scene_id/scene_group_id handles). Defaults to 'index'; the Toolbox agent passes 'scene_id'.
     */
    sceneAddressing?: SceneAddressing;
    /**
     * Whether serialized colors carry a `(name)` alongside the hex (e.g. `#ff0000 (Red)`). Defaults
     * to true; the Toolbox turns it off since its model reads hex directly.
     */
    includeColorNames?: boolean;
    /**
     * Also capture a "before" (initial) video-state snapshot at the start of each user
     * request — enabling before/after snapshot mode. Opt-in (default off); mainly for
     * evals that want the report's "View video state" to also preview the *starting*
     * video, not just the final one. Agents already in before/after mode are unaffected.
     */
    captureInitialVideoState?: boolean;
}

/**
 * RootAgent class is an agent that exists at the top of the chat hierarchy.
 * Agents coordinate work across conversations but don't call LLM directly.
 * All LLM interaction is delegated to the Conversation instances.
 */
export abstract class RootAgent {
    protected luminary: Luminary;
    protected dispatch: AppDispatch;
    protected getState: GetRootState;
    protected model: string;
    protected maxIterations = 5;
    protected shouldUseStreamingResponses = false;
    protected addBeforeAfterVideoDataDisplayMessages = false;
    protected addVideoDataDisplayMessageAfterUserRequest = false;
    protected toolsOverride?: Tool[];

    public conversation: Conversation;
    private conversationListeners: Map<string, () => void> = new Map();
    private systemMessageOverride: string | null = null;
    private resumePromise?: Promise<void>;

    public constructor(config: RootAgentConfig) {
        // Set maxIterations and streaming before creating conversation to ensure correct initialization
        if (config.maxIterations !== undefined) {
            this.maxIterations = config.maxIterations;
        }
        if (config.streaming !== undefined) {
            this.shouldUseStreamingResponses = config.streaming;
        }
        // Opt-in (never opt-out): only flip the before/after snapshot mode on, so
        // agents that already default it on (e.g. ImproveMedia) are unaffected.
        if (config.captureInitialVideoState) {
            this.addBeforeAfterVideoDataDisplayMessages = true;
        }
        this.luminary = config.luminary;
        this.dispatch = config.dispatch;
        this.getState = config.getState;
        this.model = config.model;
        this.toolsOverride = config.toolsOverride;

        const toolsToUse =
            config.toolsOverride ?? this.getTools(this.luminary, this.dispatch, this.getState);

        this.conversation = new Conversation({
            model: this.model,
            systemMessage: this.getSystemMessage(),
            tools: toolsToUse,
            maxIterations: this.maxIterations,
            luminary: this.luminary,
            parentConversation: undefined,
            task: this.getTaskName(),
            streaming: this.shouldUseStreamingResponses,
            videoId: config.videoId,
            requiresVideoState: config.requiresVideoState ?? true,
            getState: this.getState,
            sceneAddressing: config.sceneAddressing ?? 'index',
            includeColorNames: config.includeColorNames ?? true,
            enableCodeInterpreter: config.enableCodeInterpreter,
            enableThinkingSummaries: config.enableThinkingSummaries,
            reasoningEffort: config.reasoningEffort,
            // Bind the conversation to the shared save-conflict source of truth in redux
            isSaveConflict: () => getSaveConflict(this.getState()),
            reportConflict: () => this.dispatch(setSaveConflict(true)),
        });

        const unsubscribe = this.conversation.subscribe((state) => {
            this.onConversationUpdate(state);
        });
        this.conversationListeners.set(this.conversation.getId(), unsubscribe);
    }

    /**
        Optimal information flow for your system message:
        1. Role & Purpose: "You are a video editing assistant..."
        2. Core Concepts & Rules: "How our videos work..."
        3. Data Format (Reading): Video Annotation Guide (how to READ state)
        4. Change Tracking (Understanding): Diff Format Guide (how to INTERPRET changes)
        5. Task-Specific Instructions: "Your task is to..."
        6. Examples & Edge Cases: Specific scenarios...

        Why This Order?
        - Foundation First: Annotation guide teaches "what is a video state"
        - Changes Second: Diff guide teaches "how state evolves"
        - Logical Dependency: Can't understand diffs without knowing the format
     */
    protected abstract getSystemMessage(): string;

    /**
     * Whether this agent continues the video's most recent conversation rather than
     * always starting fresh. Off by default; override on the specific surface that should resume.
     */
    public get shouldResumeConversation(): boolean {
        return false;
    }

    /**
     * Whether this agent's replies offer per-turn thumbs feedback. Off by default;
     * feedback is reported under `getTaskName()`, which picks its Slack channel.
     */
    public get feedbackEnabled(): boolean {
        return false;
    }

    protected abstract getTools(
        luminary: Luminary,
        dispatch: AppDispatch,
        getState: GetRootState,
    ): Tool[];

    /**
     * The skills this agent offers, exactly as it presents them to the model
     * (after surface and feature-flag filtering). Agents with no skills return `[]`.
     * Required (not defaulted) so a new agent must make a deliberate choice, and so
     * the system prompt and tooling always share one list.
     */
    public abstract getSkills(): AnySkill[];

    /**
     * Returns the task name for metrics tracking
     */
    abstract getTaskName(): string;

    /**
     * Handles updates from child conversations
     */
    private onConversationUpdate(state: ConversationState): void {
        // This method can be used for internal tracking or logging
        // Progress is now shown through conversation messages in the UI
    }

    /**
     * Optionally get a context message to add to the conversation
     * This, for example, can provide a description of the video being edited
     * or other relevant context to help the agent make decisions.
     * @deprecated Use videoStateManager instead
     */
    protected abstract getContextMessage(): Promise<string | null>;

    /**
     * Updates the model used by this agent and its conversation.
     * Override in subclasses if additional state needs to be updated.
     */
    public setModel(newModel: string): void {
        this.model = newModel;
        this.conversation.setModel(newModel);
    }

    /**
     * Updates the reasoning effort override for this agent's conversation
     * without recreating the agent. Pass undefined to use the model default.
     */
    public setReasoningEffort(effort: string | undefined): void {
        this.conversation.setReasoningEffort(effort);
    }

    /**
     * Rebuilds the conversation's system message from the agent's current
     * getSystemMessage() without discarding the existing conversation history.
     * While a developer-tools override is set, the override wins so skill or
     * context refreshes don't silently discard the edited prompt.
     */
    public refreshSystemMessage(): void {
        this.conversation.setSystemMessage(this.systemMessageOverride ?? this.getSystemMessage());
    }

    /**
     * Replaces the system prompt with a hand-edited version (developer tools).
     * Pass null to restore the agent's default getSystemMessage(). Held in
     * memory only — never persisted — so it lasts at most for this agent
     * instance's lifetime and reverts on reload or agent recreation.
     */
    public setSystemMessageOverride(content: string | null): void {
        this.systemMessageOverride = content;
        this.refreshSystemMessage();
    }

    public getSystemMessageOverride(): string | null {
        return this.systemMessageOverride;
    }

    /**
     * Resolves once any async context the agent needs in its prompt has loaded
     * (e.g. the sketch surface's templates/voices/history). Callers should await
     * this before sending the first message so the prompt isn't built against
     * empty context. Agents with no async context resolve immediately.
     */
    public waitForContext(): Promise<void> {
        return this.resumeConversation();
    }

    /**
     * Fetch the video's latest conversation and hydrate it into this conversation.
     */
    public resumeConversation(): Promise<void> {
        if (!this.resumePromise) {
            if (!this.shouldResumeConversation || this.conversation.videoId == null) {
                this.resumePromise = Promise.resolve();
            } else {
                this.resumePromise = fetchLatestConversationData(
                    this.conversation.videoId,
                    this.conversation.getTask(),
                )
                    .then((data) => {
                        if (!data) {
                            return;
                        }
                        analytics.track('Agent Conversation Resumed', {
                            conversationId: data.id,
                            videoId: this.conversation.videoId,
                            task: this.conversation.getTask(),
                            model: data.model,
                            messageCount: data.messages.length,
                        });
                        if (data.model !== this.conversation.model) {
                            analytics.track('Agent Conversation Model Mismatch', {
                                conversationId: data.id,
                                agentModel: this.conversation.model,
                                resumedModel: data.model,
                            });
                        }
                        this.conversation.hydrate(data);
                    })
                    .catch((error) => ErrorReporter.capture(error));
            }
        }
        return this.resumePromise;
    }

    /**
     * Extra text appended to every outgoing user message, e.g. the sketch
     * surfaces' <user-preferences> block. The conversation renderer hides
     * XML-tagged sections of user messages, so tagged suffixes are not shown
     * to the user. Empty by default.
     */
    protected getUserMessageSuffix(): string {
        return '';
    }

    private appendUserMessageSuffix(userMessage: MessageContent): MessageContent {
        const suffix = this.getUserMessageSuffix();
        if (!suffix) {
            return userMessage;
        }
        if (typeof userMessage === 'string') {
            return userMessage + suffix;
        }
        return [...userMessage, { type: 'text', text: suffix }];
    }

    public async sendMessage(userMessage: MessageContent): Promise<EnhancedToolResult> {
        try {
            // Add context as system message (hidden from UI but visible to AI)
            const contextMessage = await this.getContextMessage();
            if (contextMessage) {
                this.conversation.addSystemMessage(contextMessage, true);
            }

            // Add user message to the conversation
            await this.conversation.addUserMessage(this.appendUserMessageSuffix(userMessage));

            // Execute with LLM
            const parentActionGroup = uuid();
            const result = await this.conversation.executeWithLLM({
                parentActionGroup,
                addBeforeAfterVideoDataDisplayMessages: this.addBeforeAfterVideoDataDisplayMessages,
                addVideoDataDisplayMessageAfterUserRequest:
                    this.addVideoDataDisplayMessageAfterUserRequest,
            });

            if (!result.success) {
                return {
                    result: result.error || 'Agent execution failed',
                    isSuccess: false,
                };
            }

            return {
                result: 'Agent completed successfully',
                isSuccess: true,
            };
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            this.conversation.setError(errorMessage);
            ErrorReporter.capture(error);

            return {
                result: `Agent execution failed: ${errorMessage}`,
                isSuccess: false,
            };
        } finally {
            this.dispatch(clearAllAgentActivity());
        }
    }

    public getAllTools(): Tool[] {
        return this.toolsOverride ?? this.getTools(this.luminary, this.dispatch, this.getState);
    }

    public cancel(): void {
        this.conversation.cancel();
    }
}
